import React, { useState, useMemo } from 'react';
import './SortableTable.css';

// Define possible alignment values
type ColumnAlignment = 'left' | 'center' | 'right';

export interface ColumnDefinition<T> {
  key: keyof T;
  label: string;
  sortable: boolean;
  render?: (item: T) => React.ReactNode;
  align?: ColumnAlignment; // <-- Add optional alignment property
  className?: string; // <-- Add optional className for th/td
  maxLength?: number; // <-- Add optional maxLength for cell content
}

export interface SortConfig<T> {
    key: keyof T;
    direction: 'ascending' | 'descending';
}

interface SortableTableProps<T> {
  data: T[];
  columns: ColumnDefinition<T>[];
  onRowClick?: (item: T) => void;
  defaultSortConfig?: SortConfig<T>;
  selectedRowId?: string | number | null;
  rowIdKey: keyof T;
  footer?: React.ReactNode;
}

const SortableTable = <T extends Record<string | number | symbol, any>>({
  data,
  columns,
  onRowClick,
  defaultSortConfig,
  selectedRowId,
  rowIdKey,
  footer,
}: SortableTableProps<T>) => {
  // Initialize state with defaultSortConfig if provided
  const [sortColumn, setSortColumn] = useState<keyof T | null>(defaultSortConfig?.key ?? null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    defaultSortConfig?.direction === 'ascending' ? 'asc' :
    defaultSortConfig?.direction === 'descending' ? 'desc' :
    'asc'
  );

  const sortedData = useMemo(() => {
    if (!sortColumn) return data;
    const columnToSort = columns.find(col => col.key === sortColumn);
    if (!columnToSort?.sortable) return data; // Don't sort if the column isn't marked sortable

    return [...data].sort((a, b) => {
      const aValue:any = a[sortColumn];
      const bValue:any = b[sortColumn];

      // Handle undefined/null values (optional: push them to start/end)
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1; // nulls first in asc
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1; // nulls first in asc

      let comparison = 0;

      // Use localeCompare for robust string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = (aValue === bValue) ? 0 : aValue ? 1 : -1; // true > false
      } else if (aValue instanceof Date && bValue instanceof Date) {
        // Handle Date objects
        comparison = aValue.getTime() - bValue.getTime();
      } else {
         // Attempt string conversion for other types if needed, or keep 0
         comparison = String(aValue).localeCompare(String(bValue));
      }
      return sortDirection === 'asc' ? comparison : comparison * -1;
    });
  }, [data, sortColumn, sortDirection, columns]);

  const handleSort = (columnKey: keyof T) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return; // Don't sort if column is not sortable

    if (sortColumn === columnKey) {
      // Cycle direction: asc -> desc -> unsorted (optional) -> asc ...
      // Or just toggle: asc -> desc -> asc
      setSortDirection(prevDirection => (prevDirection === 'asc' ? 'desc' : 'asc'));
      // To add unsorted state:
      // setSortDirection(prevDirection => {
      //   if (prevDirection === 'asc') return 'desc';
      //   if (prevDirection === 'desc') {
      //       setSortColumn(null); // Reset column to unsort
      //       return 'asc'; // Reset direction for next click
      //   }
      //   return 'asc'; // Should not happen if sortColumn is not null
      // });

    } else {
      setSortColumn(columnKey);
      setSortDirection('asc'); // Default to ascending when changing column
    }
  };

  const MAX_VALUE_LENGTH = 200;

  const renderFieldValue = (fieldName: string, value: any ) => {

    const valueToRender = 
    ( value === "$deriq.other$"
      ? "Others (" + fieldName  + ")"
      : (value === "" 
        ? "(Empty)" 
        : typeof value === 'string' && (value as string).length > MAX_VALUE_LENGTH
        ? `${(value as string).substring(0, MAX_VALUE_LENGTH)} ...`
      : String(value ?? '')
      )
    );

    return valueToRender
  }  

  return (
    <table className="sortable-table">
      <thead>
        <tr className="sortable-table-header-row">
          {columns.map((column) => (
            <th
              key={String(column.key)}
              onClick={() => handleSort(column.key)}
              className={`${column.sortable ? 'sortable-header' : ''} ${column.className || ''}`.trim()}
              // Apply alignment style to header cell
              style={{
                  cursor: column.sortable ? 'pointer' : 'default',
                  textAlign: column.align ?? 'left' // Default to left if not specified
              }}
            >
              {column.label}
              {sortColumn === column.key && (
                <span className="sort-indicator">
                  {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedData.length === 0 ? (
            <tr className="no-data-message">
                <td colSpan={columns.length}>
                    No data available.
                </td>
            </tr>
        ) : (
            sortedData.map((item) => {
              // Use rowIdKey to get the unique ID for the key and selection check
              const rowId = item[rowIdKey];
              const isSelected = selectedRowId !== null && rowId === selectedRowId;

              return (
                <tr
                  // Use the actual unique ID from the data via rowIdKey
                  key={String(rowId)}
                  // Conditionally add the 'selected' class
                  className={isSelected ? 'selected' : ''}
                  onClick={() => onRowClick && onRowClick(item)}
                  // Add hover effect via CSS if desired
                >
                  {columns.map((column) => (
                    <td
                      key={String(column.key)}
                      className={column.className || ''}
                      style={{ textAlign: column.align ?? 'left' }} // Default to left
                      // Add title attribute for full text on hover, especially if truncated
                      title={typeof item[column.key] === 'string' ? String(item[column.key]) : undefined}
                    >
                      {column.render
                        ? column.render(item)
                        : renderFieldValue(column.label, item[column.key])
                      }
                    </td>
                  ))}
                </tr>
              );
            })
        )}
      </tbody>
      {footer && <tfoot>{footer}</tfoot>}
    </table>
  );
};

export default SortableTable;
